import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { loadWritingCatalog, loadWritingDocument } from '../../services/writingV2';
import { LearningProgressService } from '../../services/LearningProgressService';

const CATEGORY_LABELS = {
  letters: 'Letters',
  emails: 'Emails',
  applications: 'Applications',
  'resume-or-cv': 'Resume / CV',
};

const isPositionalKey = (key) => /^(line|paragraph|item)?\d+$/.test(key);

function humanizeKey(key) {
  const spaced = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]/g, ' ');
  return spaced ? spaced[0].toUpperCase() + spaced.slice(1) : spaced;
}

// Renders one document's `sections` tree generically — a letter/email/
// application's greeting/opening/mainMessage/closing, or a CV's
// personalDetails/education/experience/skills/languages — with an optional
// bilingual line underneath each piece of text. There's no fixed schema
// here (unlike the old model/fields/blocks lesson shape this replaced), so
// this just walks whatever shape the document actually has.
function Node({ target, native, bilingual }) {
  if (target === null || target === undefined) return null;

  if (typeof target === 'string') {
    if (!target) return null;
    const nativeText = typeof native === 'string' ? native : null;
    return (
      <div className="mb-2">
        <p className="text-sm leading-relaxed text-slate-700">{target}</p>
        {bilingual && nativeText && nativeText !== target && (
          <p className="mt-0.5 text-xs italic leading-relaxed text-slate-400">{nativeText}</p>
        )}
      </div>
    );
  }

  if (Array.isArray(target)) {
    const nativeList = Array.isArray(native) ? native : [];
    return (
      <ul className="space-y-1.5">
        {target.map((item, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <li key={i} className="flex gap-2">
            <span className="mt-1.5 h-1 w-1 flex-shrink-0 rounded-full bg-slate-400" />
            <ListItem target={item} native={nativeList[i]} bilingual={bilingual} />
          </li>
        ))}
      </ul>
    );
  }

  if (typeof target === 'object') {
    const nativeObj = native && typeof native === 'object' ? native : {};
    return (
      <div>
        {Object.entries(target)
          .filter(([key]) => key !== 'heading')
          .map(([key, value]) => (
            <div key={key}>
              {!isPositionalKey(key) && (
                <p className="mb-1 mt-3 text-[11px] font-bold uppercase tracking-wide text-slate-400">
                  {humanizeKey(key)}
                </p>
              )}
              <Node target={value} native={nativeObj[key]} bilingual={bilingual} />
            </div>
          ))}
      </div>
    );
  }

  return null;
}

function ListItem({ target, native, bilingual }) {
  if (typeof target === 'string') {
    const nativeText = typeof native === 'string' ? native : null;
    return (
      <div>
        <p className="text-sm text-slate-700">{target}</p>
        {bilingual && nativeText && nativeText !== target && (
          <p className="text-xs italic text-slate-400">{nativeText}</p>
        )}
      </div>
    );
  }
  if (target && typeof target === 'object') {
    const nativeObj = native && typeof native === 'object' ? native : {};
    const parts = [];
    const nativeParts = [];
    const subLists = [];
    Object.entries(target).forEach(([key, value]) => {
      if (typeof value === 'string' && value) {
        parts.push(value);
        if (typeof nativeObj[key] === 'string' && nativeObj[key]) nativeParts.push(nativeObj[key]);
      } else if (Array.isArray(value)) {
        subLists.push([key, value]);
      }
    });
    return (
      <div>
        <p className="text-sm font-bold text-slate-800">{parts.join(' · ')}</p>
        {bilingual && nativeParts.length > 0 && (
          <p className="text-xs italic text-slate-400">{nativeParts.join(' · ')}</p>
        )}
        {subLists.map(([key, value]) => (
          <div key={key} className="ml-2 mt-1">
            <Node target={value} native={nativeObj[key]} bilingual={bilingual} />
          </div>
        ))}
      </div>
    );
  }
  return null;
}

function useCatalog() {
  const [catalog, setCatalog] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    loadWritingCatalog()
      .then((items) => active && setCatalog(items))
      .catch(() => active && setError(true));
    return () => { active = false; };
  }, []);

  return { catalog, error };
}

export default function WritingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { languagePrefs } = useAuth();
  const target = languagePrefs?.targetLanguage || 'en';
  const native = languagePrefs?.nativeLanguage || 'en';

  const { catalog, error: catalogError } = useCatalog();
  const [activeCategory, setActiveCategory] = useState('letters');
  const [document, setDocument] = useState(null);
  const [nativeDocument, setNativeDocument] = useState(null);
  const [bilingual, setBilingual] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const categories = useMemo(() => {
    const seen = [];
    (catalog || []).forEach((item) => {
      if (item.category && !seen.includes(item.category)) seen.push(item.category);
    });
    return seen;
  }, [catalog]);

  useEffect(() => {
    if (categories.length > 0 && !categories.includes(activeCategory)) {
      setActiveCategory(categories[0]);
    }
  }, [categories, activeCategory]);

  const items = useMemo(
    () => (catalog || []).filter((item) => item.category === activeCategory),
    [catalog, activeCategory],
  );

  async function openDocument(item) {
    setError(false);
    setLoading(true);
    setBilingual(false);
    try {
      const targetDoc = await loadWritingDocument(item.path, target);
      const nativeDoc = native === target ? targetDoc : await loadWritingDocument(item.path, native).catch(() => null);
      setDocument({ ...targetDoc, _id: item.id });
      setNativeDocument(nativeDoc);
      LearningProgressService.record(`${native}_${target}`, 'writing', 1, {
        activityId: `document:${item.id}`,
      });
    } catch (_) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }

  if (document) {
    return (
      <div className="min-h-screen bg-slate-50 px-4 py-8">
        <div className="mx-auto max-w-3xl">
          <button
            type="button"
            onClick={() => setDocument(null)}
            className="mb-5 font-bold text-indigo-600"
          >
            {t('writing.allLessons', { defaultValue: 'All documents' })}
          </button>
          <div className="rounded-3xl bg-gradient-to-br from-violet-600 to-indigo-700 p-7 text-white shadow-xl">
            <h1 className="text-3xl font-black">{document.title}</h1>
            {bilingual && nativeDocument?.title && nativeDocument.title !== document.title && (
              <p className="mt-1 font-bold text-violet-100">{nativeDocument.title}</p>
            )}
            <p className="mt-2 text-violet-100">{document.purpose}</p>
            {bilingual && nativeDocument?.purpose && nativeDocument.purpose !== document.purpose && (
              <p className="mt-1 text-sm italic text-violet-200">{nativeDocument.purpose}</p>
            )}
            {nativeDocument && (
              <button
                type="button"
                onClick={() => setBilingual((v) => !v)}
                className={`mt-4 inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-xs font-bold transition ${
                  bilingual ? 'bg-white text-violet-700' : 'bg-white/15 text-white hover:bg-white/25'
                }`}
              >
                🌐 {bilingual ? t('conversationsPage.bilingualOn', { defaultValue: 'Bilingual on' }) : t('conversationsPage.bilingual', { defaultValue: 'Bilingual' })}
              </button>
            )}
          </div>

          <section className="mt-6 rounded-3xl border bg-white p-6 shadow-sm">
            {Object.entries(document.sections || {})
              .filter(([key]) => key !== 'heading')
              .map(([key, value]) => (
                <div key={key} className="mb-5 last:mb-0">
                  <h2 className="mb-2 text-sm font-black uppercase tracking-wide text-violet-700">
                    {humanizeKey(key)}
                  </h2>
                  <Node target={value} native={nativeDocument?.sections?.[key]} bilingual={bilingual} />
                </div>
              ))}
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8">
      <div className="mx-auto max-w-5xl">
        <button type="button" onClick={() => navigate('/')} className="mb-5 font-bold text-indigo-600">
          {t('writing.learningPath', { defaultValue: 'Back to learning path' })}
        </button>
        <div className="rounded-3xl bg-gradient-to-br from-violet-600 to-indigo-700 p-8 text-white">
          <p className="text-4xl">✍️</p>
          <h1 className="mt-3 text-3xl font-black">{t('writing.title', { defaultValue: 'Writing Examples' })}</h1>
          <p className="mt-2 max-w-2xl text-violet-100">
            {t('writing.description', { defaultValue: 'Read worked example documents in your target language, with a bilingual toggle for your own.' })}
          </p>
        </div>

        <div className="mt-6 flex gap-2 overflow-x-auto">
          {categories.map((category) => (
            <button
              key={category}
              type="button"
              onClick={() => setActiveCategory(category)}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-bold ${
                activeCategory === category ? 'bg-violet-600 text-white' : 'border bg-white text-slate-600'
              }`}
            >
              {CATEGORY_LABELS[category] || category}
            </button>
          ))}
        </div>

        {(catalogError || error) && (
          <p className="mt-6 rounded-xl bg-rose-50 p-4 text-rose-700">
            {t('writing.loadError', { defaultValue: 'Could not load this content. Please try again.' })}
          </p>
        )}
        {!catalog && !catalogError && (
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            {[1, 2, 3, 4].map((i) => <div key={i} className="h-28 animate-pulse rounded-2xl bg-slate-100" />)}
          </div>
        )}
        {catalog && !catalogError && (
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            {items.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => openDocument(item)}
                disabled={loading}
                className="rounded-2xl border bg-white p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-violet-300 hover:shadow-md disabled:opacity-60"
              >
                <div className="flex items-center justify-between">
                  <span className="text-2xl">{item.emoji}</span>
                  <span className="text-slate-300">→</span>
                </div>
                <h2 className="mt-3 font-black text-slate-900">{item.label}</h2>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
